import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useNavigate, useParams } from 'react-router';

import { deckKeys } from '@/entities/deck';
import { useSession } from '@/entities/session';
import {
  AddCardForm,
  DeckDangerZone,
  DeckGeneralForm,
  DeleteCardButton,
} from '@/features/deck-editor';
import { LeechList } from '@/features/leeches';
import { getDeck, listCards } from '@/shared/api/decks';
import { ErrorMessage, LinkButton } from '@/shared/ui';

import styles from './DeckPage.module.css';

function CardsTab({ deckId }: { deckId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const cards = useQuery({
    queryKey: deckKeys.cards(deckId),
    queryFn: ({ signal }) => listCards(deckId, signal),
  });

  return (
    <>
      <section className={styles.box} aria-labelledby="add-card">
        <h2 id="add-card">{t('decks.newCard')}</h2>
        <AddCardForm deckId={deckId} />
      </section>

      <section aria-labelledby="cards-title">
        <h2 id="cards-title">{t('decks.cardsTitle')}</h2>
        {cards.isError && <ErrorMessage error={cards.error} />}
        {cards.data?.length === 0 && <p className={styles.muted}>{t('decks.noCards')}</p>}
        <ul className={styles.cards}>
          {cards.data?.map((card) => (
            <li className={styles.card} key={card.id}>
              <div>
                <p className={styles.text}>{card.front}</p>
                <p className={[styles.text, styles.muted].join(' ')}>{card.back}</p>
              </div>
              <DeleteCardButton deckId={deckId} cardId={card.id} front={card.front} />
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

/**
 * One deck, laid out like a repository on GitHub: owner and name on top, the
 * main action at the side, and tabs for its cards, its health and its
 * settings.
 */
export function DeckPage({ tab }: { tab: 'cards' | 'health' | 'settings' }): React.JSX.Element {
  const { t } = useTranslation();
  const { deckId = '' } = useParams();
  const navigate = useNavigate();
  const { data: user } = useSession();

  const deck = useQuery({
    queryKey: deckKeys.one(deckId),
    queryFn: ({ signal }) => getDeck(deckId, signal),
  });

  if (deck.isError) {
    return <ErrorMessage error={deck.error} />;
  }

  const tabClass = ({ isActive }: { isActive: boolean }): string =>
    [styles.tab, isActive ? styles.tabActive : ''].join(' ');

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>
            {user && (
              <>
                <Link to="/decks" className={styles.owner}>
                  @{user.username}
                </Link>
                <span className={styles.slash}> / </span>
              </>
            )}
            <span>{deck.data?.title ?? '…'}</span>
          </h1>
          {deck.data?.description && <p className={styles.description}>{deck.data.description}</p>}
        </div>
        {deck.data && deck.data.card_count > 0 && (
          <LinkButton
            to={`/review/${deckId}`}
            variant="primary"
            aria-label={t('learn.startDeck', { title: deck.data.title })}
          >
            {t('decks.study')}
          </LinkButton>
        )}
      </header>

      <nav className={styles.tabs} aria-label={t('decks.sections')}>
        <NavLink end to={`/decks/${deckId}`} className={tabClass}>
          {t('decks.cardsTitle')}
          {deck.data && <span className={styles.counter}>{deck.data.card_count}</span>}
        </NavLink>
        <NavLink to={`/decks/${deckId}/health`} className={tabClass}>
          {t('health.tab')}
        </NavLink>
        <NavLink to={`/decks/${deckId}/settings`} className={tabClass}>
          {t('decks.settings')}
        </NavLink>
      </nav>

      {tab === 'cards' && deck.data && <CardsTab deckId={deckId} />}

      {tab === 'health' && deck.data && (
        <section aria-labelledby="deck-health">
          <h2 id="deck-health">{t('health.deckTitle')}</h2>
          <LeechList deckId={deckId} />
        </section>
      )}

      {tab === 'settings' && deck.data && (
        <>
          <section aria-labelledby="deck-general">
            <h2 id="deck-general">{t('decks.general')}</h2>
            <DeckGeneralForm key={deck.data.id} deck={deck.data} />
          </section>
          <section aria-labelledby="deck-danger">
            <h2 id="deck-danger">{t('decks.dangerZone')}</h2>
            <DeckDangerZone
              deck={deck.data}
              onDeleted={() => {
                void navigate('/decks');
              }}
            />
          </section>
        </>
      )}
    </div>
  );
}
